// Presentational pieces for the weekly-routes validation UI. Page-private.
import React from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../utils/styledComponent';
import theme from '../../theme';
import StopStatusPill from '../../components/atoms/StopStatusPill';

// ---------------------------------------------------------------------------
// ValidationSummary
// ---------------------------------------------------------------------------

const TONE_STYLES = {
  danger: {
    color: theme.colors.danger,
    background: theme.colors.dangerBg,
    borderColor: theme.colors.dangerBorder,
  },
  // Non-blocking heads-ups.
  warning: {
    color: theme.colors.cautionText,
    background: theme.colors.cautionBg,
    borderColor: theme.colors.caution,
  },
  success: {
    color: theme.colors.successText,
    background: theme.colors.successBg,
    borderColor: theme.colors.success,
  },
  info: {
    color: theme.colors.textMuted,
    background: theme.colors.bgMuted,
    borderColor: theme.colors.border,
  },
};

const toneClassStyles = Object.keys(TONE_STYLES).reduce((acc, tone) => {
  acc[`&.vsum-${tone}`] = {
    color: TONE_STYLES[tone].color,
    background: TONE_STYLES[tone].background,
    border: `1px solid ${TONE_STYLES[tone].borderColor}`,
  };
  return acc;
}, {});

const StyledSummary = styledComponent('div', {
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing.gapMd,
  padding: theme.spacing.cellPadding,
  borderRadius: theme.radii.inner,
  marginBottom: theme.spacing.fieldGap,

  '& .vsum-icon': {
    display: 'inline-flex',
    flexShrink: 0,
    marginTop: theme.spacing.gapXs,
  },

  '& .vsum-title': {
    ...theme.typography.label,
    lineHeight: theme.lineHeights.compact,
  },

  '& .vsum-body': {
    ...theme.typography.body,
    marginTop: theme.spacing.labelGap,
  },

  ...toneClassStyles,
});

const DangerIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M7 1 L13.3 12.3 H0.7 Z" fill="currentColor" fillRule="evenodd" />
    <path
      d="M7 5.4 V8.6 M7 10 V11.2"
      stroke={theme.colors.white}
      strokeWidth="1.3"
      strokeLinecap="round"
    />
  </svg>
);

const SuccessIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="7" cy="7" r="6.3" fill="currentColor" />
    <path
      d="M4.2 7.2 L6.2 9.2 L9.8 5.2"
      stroke={theme.colors.white}
      strokeWidth="1.4"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const InfoIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="7" cy="7" r="6.3" fill="currentColor" />
    <path
      d="M7 6.2 V10 M7 3.6 V4.8"
      stroke={theme.colors.white}
      strokeWidth="1.3"
      strokeLinecap="round"
    />
  </svg>
);

const ICONS = {
  danger: DangerIcon,
  warning: DangerIcon,
  success: SuccessIcon,
  info: InfoIcon,
};

export function ValidationSummary({ tone, title, children }) {
  const Icon = ICONS[tone];
  return (
    <StyledSummary
      className={`vsum-${tone}`}
      role={tone === 'danger' ? 'alert' : 'status'}
      data-testid="validation-summary"
    >
      <span className="vsum-icon">
        <Icon />
      </span>
      <div>
        <div className="vsum-title">{title}</div>
        {children && <div className="vsum-body">{children}</div>}
      </div>
    </StyledSummary>
  );
}

ValidationSummary.propTypes = {
  tone: PropTypes.oneOf(['danger', 'warning', 'success', 'info']).isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
};

ValidationSummary.defaultProps = {
  children: null,
};

// ---------------------------------------------------------------------------
// RowStatusCell
// ---------------------------------------------------------------------------

const StyledToggle = styledComponent('button', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing.gapSm,
  padding: 0,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  borderRadius: theme.radii.pill,

  '&:focus-visible': {
    outline: 'none',
    boxShadow: `0 0 0 2px ${theme.colors.infoBlue}33`,
  },

  '& .rsc-chevron': {
    display: 'inline-flex',
    color: theme.colors.danger,
    transition: 'transform 0.15s ease',
  },

  '&[aria-expanded="true"] .rsc-chevron': {
    transform: 'rotate(180deg)',
  },
});

const Chevron = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
    aria-hidden="true"
    focusable="false"
    className="rsc-chevron"
  >
    <path
      d="M2 3.5 L5 6.5 L8 3.5"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export function RowStatusCell({ errorCount, expanded, onToggle }) {
  if (errorCount === 0) {
    return <StopStatusPill tone="on-track" label="Valid" />;
  }
  const label = `${errorCount} error${errorCount > 1 ? 's' : ''}`;
  return (
    <StyledToggle
      type="button"
      aria-expanded={expanded}
      onClick={onToggle}
      title={expanded ? 'Hide error details' : 'Show error details'}
      data-testid="row-status-toggle"
    >
      <StopStatusPill tone="off-track" label={label} />
      <Chevron />
    </StyledToggle>
  );
}

RowStatusCell.propTypes = {
  errorCount: PropTypes.number.isRequired,
  expanded: PropTypes.bool,
  onToggle: PropTypes.func,
};

RowStatusCell.defaultProps = {
  expanded: false,
  onToggle: () => {},
};

// ---------------------------------------------------------------------------
// ErrorSubRow
// ---------------------------------------------------------------------------

const StyledSubRow = styledComponent('tr', {
  '& .esr-cell': {
    background: theme.colors.dangerHoverBg,
    borderLeft: `${theme.spacing.gapXs} solid ${theme.colors.danger}`,
    color: theme.colors.danger,
    padding: theme.spacing.cellPadding,
  },

  '& .esr-list': {
    margin: 0,
    paddingLeft: theme.spacing.fieldGap,
    ...theme.typography.label,
    lineHeight: theme.lineHeights.body,
  },
});

export function ErrorSubRow({ colSpan, lineNumber, errors }) {
  return (
    <StyledSubRow data-testid={`error-subrow-${lineNumber}`}>
      <td className="esr-cell" colSpan={colSpan}>
        <ul className="esr-list">
          {errors.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      </td>
    </StyledSubRow>
  );
}

ErrorSubRow.propTypes = {
  colSpan: PropTypes.number.isRequired,
  lineNumber: PropTypes.number.isRequired,
  errors: PropTypes.arrayOf(PropTypes.string).isRequired,
};
